import React from "react";
import { ExternalLink, Building2, ShieldCheck, Truck, BookOpen, ArrowRight } from "lucide-react";
import useSeo from "../../hooks/useSeo";

const BRANDS = [
    {
        name: "Otel Ürünleri",
        url: "https://otel-urunleri.com.tr",
        tagline: "Geniş otel ürünleri katalogu",
        desc: "Kahvaltı setlerinden tekstile, banyo amenities'den dekorasyon ürünlerine kadar otelinizin ihtiyaç duyacağı tüm ürün kategorilerini tek çatı altında bulun.",
        features: [
            "Geniş ürün kategorileri",
            "Otelci dostu fiyatlandırma",
            "Hızlı sevkiyat seçenekleri",
            "Sektör bazlı kürasyon",
        ],
        Icon: Building2,
        accent: "teal",
    },
    {
        name: "Otel Malzemeleri",
        url: "https://otel-malzemeleri.com",
        tagline: "Premium B2B otel tedariki",
        desc: "Markalı çözümler, kurumsal müşteri yönetimi ve otelci ölçeğinde özel fiyatlama. 4+ yıldız tesisler için özel olarak seçilmiş tedarikçi ağı.",
        features: [
            "Premium marka ortaklıkları",
            "Kurumsal hesap yönetimi",
            "Volume-based fiyatlandırma",
            "İade & değişim destek",
        ],
        Icon: ShieldCheck,
        accent: "gold",
    },
    {
        name: "Otel Ekipmanları",
        url: "https://otel-ekipmanlari.com",
        tagline: "Otel açılış danışmanlığı + ekipman rehberi",
        desc: "Yeni açılış aşamasındaki tesisler için teknik ekipman uzmanlığı, kapsamlı checklist'ler ve A'dan Z'ye fizibilite desteği. Mutfaktan teknik altyapıya tam rehberlik.",
        features: [
            "Açılış danışmanlığı",
            "Detaylı ekipman checklist",
            "Marka karşılaştırma",
            "Servis ortaklıkları",
        ],
        Icon: Truck,
        accent: "deep-blue",
    },
];

export default function Brands() {
    useSeo({
        title: "Markalarımız",
        description: "Otel Pro Ekosistemi — otel-urunleri.com.tr, otel-malzemeleri.com, otel-ekipmanlari.com kardeş markaları.",
        path: "/markalarimiz",
    });

    return (
        <div className="bg-light-bg" data-testid="brands-page">
            <section className="bg-dark-navy text-white relative overflow-hidden">
                <div className="absolute inset-0 bg-gradient-to-br from-[#08111d] via-[#0c2438] to-[#123b52]" />
                <div className="relative max-w-5xl mx-auto px-6 py-16 lg:py-20 text-center">
                    <span className="text-xs uppercase tracking-[0.25em] text-teal font-semibold">Markalarımız</span>
                    <h1 className="mt-3 text-3xl sm:text-5xl font-bold tracking-tight">Otel Pro Ekosistemi</h1>
                    <p className="mt-5 text-white/80 max-w-2xl mx-auto">
                        4 farklı pencere, tek bir sektör vizyonu. Her bir alan için özelleşmiş kaynaklarla sektörün ihtiyaçlarına bütünsel cevap veriyoruz.
                    </p>
                </div>
            </section>

            <section className="py-14">
                <div className="max-w-6xl mx-auto px-6 space-y-6">
                    {BRANDS.map((b, i) => (
                        <BrandCard key={b.url} brand={b} reversed={i % 2 === 1} />
                    ))}
                </div>
            </section>

            <section className="bg-white py-14 border-t border-slate-200">
                <div className="max-w-3xl mx-auto px-6 text-center">
                    <BookOpen className="w-10 h-10 mx-auto text-teal" />
                    <h2 className="mt-3 text-2xl font-bold text-dark-navy">Otel Pro — Bilgi & Veri</h2>
                    <p className="mt-2 text-deep-blue/80">
                        Bu site (otel-pro.com), kardeş markaların aksine bir tedarik veya ürün satış kanalı değildir. Saf olarak sektör verisi, karar destek araçları ve yayın için tasarlanmıştır.
                    </p>
                </div>
            </section>
        </div>
    );
}

function BrandCard({ brand, reversed }) {
    const Icon = brand.Icon;
    const accentBg = brand.accent === "gold" ? "bg-gold/10 text-gold-700" : brand.accent === "deep-blue" ? "bg-deep-blue/10 text-deep-blue" : "bg-teal/10 text-teal-700";
    const accentBtn = brand.accent === "gold" ? "bg-gold hover:bg-gold-700 text-dark-navy" : brand.accent === "deep-blue" ? "bg-deep-blue hover:bg-dark-navy text-white" : "bg-teal hover:bg-teal-700 text-dark-navy";

    return (
        <div className={`lift-card bg-white rounded-xl border border-slate-200 shadow-md hover:shadow-xl overflow-hidden grid md:grid-cols-12 ${reversed ? "md:[direction:rtl]" : ""}`} data-testid={`brand-card-${brand.name}`}>
            {/* Left visual */}
            <div className="md:col-span-5 bg-gradient-to-br from-dark-navy via-deep-blue to-dark-navy p-10 flex flex-col items-center justify-center text-white relative overflow-hidden md:[direction:ltr]">
                <div className="absolute -top-20 -right-20 w-64 h-64 rounded-full bg-teal/15 blur-3xl" />
                <span className="relative inline-flex w-20 h-20 rounded-xl bg-white/5 border border-white/10 items-center justify-center backdrop-blur">
                    <Icon className="w-10 h-10 text-teal" />
                </span>
                <h3 className="relative mt-6 text-2xl font-bold text-center">{brand.name}</h3>
                <p className="relative mt-1 text-xs text-teal text-center uppercase tracking-wider font-semibold">{brand.tagline}</p>
            </div>
            {/* Right detail */}
            <div className="md:col-span-7 p-7 md:[direction:ltr]">
                <p className="text-deep-blue/85 leading-relaxed">{brand.desc}</p>
                <ul className="mt-5 grid grid-cols-2 gap-3">
                    {brand.features.map((f) => (
                        <li key={f} className="text-sm text-dark-navy flex items-start gap-2">
                            <span className={`mt-0.5 inline-flex w-5 h-5 rounded-md items-center justify-center text-[10px] font-bold ${accentBg}`}>✓</span>
                            {f}
                        </li>
                    ))}
                </ul>
                <div className="mt-7">
                    <a
                        href={brand.url}
                        target="_blank"
                        rel="nofollow noopener noreferrer"
                        data-testid={`brand-visit-${brand.name}`}
                        className={`inline-flex items-center gap-2 ${accentBtn} font-bold text-sm px-5 py-3 rounded-md shadow-sm`}
                    >
                        Ziyaret Et <ExternalLink className="w-4 h-4" />
                    </a>
                </div>
            </div>
        </div>
    );
}
